/**
 * src/pricingtable/utils/getPlanCtaState.js — 2026-03-26
 * ------------------------------------------------------------------
 * PURPOSE
 * ------------------------------------------------------------------
 * Centralized CTA-state resolver for the authenticated pricing page.
 *
 * WHY THIS FILE EXISTS
 * ------------------------------------------------------------------
 * We need ONE source of truth for CTA labels/behavior across:
 *   • top pricing cards
 *   • compare-table summary row
 *
 * This keeps the in-app pricing page enterprise-grade by ensuring:
 *   • consistent labels
 *   • consistent disabled states
 *   • consistent handling for:
 *       - current plans
 *       - trial users
 *       - failed billing / past_due / unpaid
 *       - restart states (canceled / incomplete_expired)
 *       - contact-sales plans
 *
 * PRODUCT COPY STRATEGY
 * ------------------------------------------------------------------
 * Per the agreed PLG direction:
 *   • Current plan          → "Current Plan"
 *   • Selectable self-serve → "Select Plan"
 *   • Contact-sales plan    → "Contact Sales"
 *
 * Important:
 * - We intentionally keep the button copy simple.
 * - More detailed upgrade / downgrade / restart messaging belongs
 *   in the review drawer, not on every pricing CTA.
 *
 * RETURN SHAPE
 * ------------------------------------------------------------------
 * {
 *   label: string,
 *   disabled: boolean,
 *   reason: string | null,
 *   action: "current" | "select" | "contact_sales"
 * }
 */

export function normalizeBillingInterval(value) {
  const raw = (value || "").toString().trim().toLowerCase();

  if (raw === "annual" || raw === "year" || raw === "yearly") return "annual";
  if (raw === "monthly" || raw === "month") return "monthly";

  return null;
}

export function isRestartLikeStatus(status) {
  const raw = (status || "").toString().trim().toLowerCase();

  return raw === "canceled" || raw === "incomplete_expired";
}

export function isFailedBillingStatus(status) {
  const raw = (status || "").toString().trim().toLowerCase();

  return (
    raw === "past_due" ||
    raw === "unpaid" ||
    raw === "incomplete"
  );
}

export function isSalesLedCurrentSubscription(currentSubscription, planConfigs) {
  const currentPlanKey = (
    currentSubscription?.planKey ||
    currentSubscription?.planName ||
    ""
  )
    .toString()
    .trim()
    .toLowerCase();

  if (!currentPlanKey) return false;

  const currentCfg = planConfigs?.[currentPlanKey] || {};

  return (
    currentCfg?.availability?.selfServe === false ||
    currentCfg?.cta?.type === "contact_sales" ||
    currentCfg?.pricingDisplay === "contact"
  );
}

/**
 * Resolve in-app CTA behavior for a plan option.
 *
 * @param {Object} params
 * @param {Object} params.plan
 * @param {string} params.planKey
 * @param {string|null} params.planPriceId
 * @param {Object|null} params.currentSubscription
 * @param {boolean} params.isPublic
 * @param {boolean} params.isContactSalesPlan
 * @param {boolean} params.isSeatCountUpdated
 * @param {Object} params.planConfigs
 * @returns {{label:string, disabled:boolean, reason:string|null, action:string}}
 */
export function getPlanCtaState({
  plan,
  planKey,
  planPriceId,
  currentSubscription,
  isPublic,
  isContactSalesPlan,
  isSeatCountUpdated,
  planConfigs,
}) {
  const status = (currentSubscription?.status || "").toString().trim().toLowerCase();
  const currentPriceId = currentSubscription?.priceId || null;

  const currentInterval = normalizeBillingInterval(currentSubscription?.interval);
  const targetInterval = normalizeBillingInterval(plan?.interval);

  const currentSubscriptionIsSalesLed =
    isSalesLedCurrentSubscription(currentSubscription, planConfigs);

  const isTrialing = status === "trialing";
  const isRestart = isRestartLikeStatus(status);
  const hasFailedBilling = isFailedBillingStatus(status);

  const isCurrentPlan =
    !!planPriceId &&
    !!currentPriceId &&
    planPriceId === currentPriceId;

  const isSameInterval =
    !!currentInterval &&
    !!targetInterval &&
    currentInterval === targetInterval;

  const isEffectiveCurrent =
    isCurrentPlan &&
    !isRestart;

  /**
   * PUBLIC / marketing pricing page behavior
   * ----------------------------------------
   * Keep classic PLG CTA labels there.
   */
  if (isPublic) {
    if (isContactSalesPlan) {
      return {
        label: plan?.cta?.label || "Contact Sales",
        disabled: false,
        reason: null,
        action: "contact_sales",
      };
    }

    return {
      label: "Get Started",
      disabled: false,
      reason: null,
      action: "select",
    };
  }

  /**
   * AUTHENTICATED / in-app pricing page behavior
   * --------------------------------------------
   * Keep buttons simple:
   *   • Current Plan
   *   • Select Plan
   *   • Contact Sales
   *
   * More nuanced messaging is handled in the drawer.
   */

  /**
   * CURRENT SALES-LED SUBSCRIPTION RULE
   * -----------------------------------
   * If the customer is currently on a sales-led / non-self-serve plan
   * (for example Enterprise), we should not present a normal self-serve
   * "Select Plan" path for switching plans in-app.
   *
   * Instead, route them back into the controlled sales-led path.
   *
   * This prevents the UI from opening a self-serve review drawer that will
   * later be blocked by ReviewChangesDrawer2.
   */
  if (currentSubscriptionIsSalesLed) {
    return {
      label: "Contact Sales",
      disabled: false,
      reason: "current_subscription_sales_led",
      action: "contact_sales",
    };
  }

  if (isContactSalesPlan) {
    return {
      label: plan?.cta?.label || "Contact Sales",
      disabled: false,
      reason: null,
      action: "contact_sales",
    };
  }

  /**
   * If the user is on the current active plan and nothing changed,
   * the CTA should be disabled.
   *
   * IMPORTANT:
   * - trialing users should still be able to select plans
   * - failed billing users should still be able to select plans
   * - restart users should still be able to select plans
   */
  if (
    isEffectiveCurrent &&
    !isTrialing &&
    !hasFailedBilling &&
    !isRestart &&
    !isSeatCountUpdated &&
    isSameInterval
  ) {
    return {
      label: "Current Plan",
      disabled: true,
      reason: "already_current_plan",
      action: "current",
    };
  }

  /**
   * Trial users, failed-billing users, and restart states
   * can always move into the drawer with a simple PLG CTA.
   */
  if (isTrialing || hasFailedBilling || isRestart) {
    return {
      label: "Select Plan",
      disabled: false,
      reason: null,
      action: "select",
    };
  }

  /**
   * Default selectable paid-state behavior.
   */
  return {
    label: "Select Plan",
    disabled: false,
    reason: null,
    action: "select",
  };
}